import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeFrExtra from '@angular/common/locales/extra/fr';
import * as Sentry from "@sentry/angular";
import { vendorHttpInterceptor } from './app/services/http-interceptor';
import { provideAppTransloco } from './app/i18n/transloco.config';
import { PushNavigatorService } from './app/services/push-navigator.service';

// Locale FR (DatePipe avec format 'fr' nécessite ces données)
registerLocaleData(localeFr, 'fr', localeFrExtra);

Sentry.init({
  dsn: "https://2c1e3d61dbd56951c79dd1b52b51afc5@o4506978209693696.ingest.us.sentry.io/4508616051458048",
  integrations: [
    Sentry.browserTracingIntegration(),
    // ⚠️ Sentry Session Replay (rrweb) VOLONTAIREMENT DÉSACTIVÉ.
    //
    // CAUSE RACINE du gel du navigateur après connexion ("la page ne répond
    // plus") : `replayIntegration()` embarque rrweb, dont le MutationObserver
    // `processMutations` partait en boucle synchrone serrée sur le tableau de
    // bord refondu (Chart.js + count-up requestAnimationFrame + intervalles +
    // listes recalculées) → thread principal saturé, onglet figé.
    // Diagnostic CDP : Debugger.pause s'arrêtait dans `processMutations` /
    // `emit` (Mirror.idNodeMap), Profiler.stop ne rendait jamais la main.
    // De plus `replaysOnErrorSampleRate: 1.0` activait l'enregistrement à
    // CHAQUE session contenant une erreur → reproduction quasi systématique.
    //
    // Le reporting d'erreurs et le tracing (browserTracing) restent actifs ;
    // seul l'enregistrement vidéo rrweb (non essentiel) est retiré. Ne pas
    // réactiver sans corriger en amont la boucle rrweb.
    // Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["vendeur.site"],
  // Session Replay désactivé (cf. note ci-dessus) — taux à 0 par sécurité au
  // cas où l'intégration serait ré-ajoutée par inadvertance.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
});

// Bootstrap the Angular application
bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),
    // ⚠️ Animations fournies UNE SEULE FOIS via provideAnimations().
    //
    // CAUSE RACINE du gel après connexion : le tableau provider contenait À LA
    // FOIS `BrowserAnimationsModule` ET `provideAnimations()` → le moteur
    // d'animations était enregistré deux fois, empilant deux
    // AnimationRendererFactory. À chaque rendu, le `end()`/`flush()` de l'une
    // relançait le flush de l'autre → boucle synchrone
    // `_flushAnimations → processLeaveNode → querySelectorAll` pendant le tick
    // de détection de changement (diagnostic CDP : Debugger.pause bloqué dans
    // le moteur @angular/animations, Profiler.stop sans retour). Le DOM riche
    // du dashboard (cartes/listes + triggers [@fadeInOut]/[@slideIn]) rendait
    // le gel systématique.
    //
    // `provideAnimations()` (API standalone) remplace intégralement
    // `BrowserAnimationsModule` : tous les [@trigger] continuent de fonctionner.
    provideAnimations(),
    { provide: LOCALE_ID, useValue: 'fr' },
    provideHttpClient(withInterceptors([vendorHttpInterceptor])),
    // Service Worker PWA ACTIVÉ — assure :
    //   • Web Push (notifications natives via SwPush + VAPID)
    //   • Background Sync (replay des actions offline)
    //   • Periodic Background Sync (keep-fresh badge / data)
    //   • Purge one-shot des anciens caches stales (cf. flag IDB vd-sw-purged-<version>)
    //
    // Le fichier `src/ngsw-worker.js` n'est plus un kill-switch destructif :
    // il s'enregistre durablement et n'effectue une purge initiale qu'UNE
    // fois par version (idempotent, flag persistant en IndexedDB). Il
    // n'intercepte pas les requêtes (laissé au navigateur) pour éviter
    // toute interaction avec l'authentification ou les WebSockets.
    provideServiceWorker('ngsw-worker.js', {
      enabled: true,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    // PushNavigatorService — bridge SW → Router pour la navigation
    // déclenchée par un clic sur une notification push (corps OU
    // bouton d'action). Doit être init au boot pour que l'écouteur
    // ``navigator.serviceWorker.message`` soit attaché tôt — sinon
    // les ``postMessage`` envoyés par le SW au moment du clic peuvent
    // être perdus si l'app n'a pas encore chargé son service.
    {
      provide: APP_INITIALIZER,
      useFactory: (nav: PushNavigatorService) => () => nav.init(),
      deps: [PushNavigatorService],
      multi: true,
    },
    // i18n (Transloco) — enregistré au niveau du bootstrap réel pour que
    // tous les chunks lazy partagent la même instance de TranslocoService.
    // (Note historique : `app.config.ts` n'est pas utilisé par ce bootstrap,
    // seuls les providers listés ici prennent effet.)
    ...provideAppTransloco(),
  ]
})
.catch((err) => console.error(err));
